import { clone, exists } from '@jack-henry/frontend-utils/functions';
import { UserInformationModelDto as BoCompanyUserInformationModelDto } from '@treasury/api/bo';
import { UserInformationModelDto as ConsumerCompanyUserInformationModelDto } from '@treasury/api/channel';
import { DomainEntity } from '../../../shared';

export const nullConsumerCompanyUserInformationModel: ConsumerCompanyUserInformationModelDto = {
    userId: 0,
    name: '',
    loginId: '',
    department: '',
    email: '',
    phone: '',
    extension: '',
    fax: '',
    isMfaBlocked: false,
    isSuperUser: false,
    isAccountMaskingConfigurable: false,
    isMethodOfVerificationConfigurable: false,
    isAdmin: false,
    copyUserId: 0,
    isEnrolled: false,
    isActive: false,
    success: false,
};

export const nullBoCompanyUserInformationModel: BoCompanyUserInformationModelDto = {
    ...nullConsumerCompanyUserInformationModel,
    pendingEditOfUserUniqueId: '',
    hasLoggedIn: false,
};

export class CompanyUserInformation extends DomainEntity<
    ConsumerCompanyUserInformationModelDto | BoCompanyUserInformationModelDto
> {
    constructor(dto = clone(nullConsumerCompanyUserInformationModel)) {
        super(dto);
    }

    public createDefault(): this {
        return new CompanyUserInformation(clone(nullConsumerCompanyUserInformationModel)) as this;
    }

    public get loginId() {
        return this.dto.loginId || '';
    }

    public set loginId(value: string) {
        this.dto.loginId = value;
    }

    public get name() {
        return this.dto.name || '';
    }

    public set name(value: string) {
        this.dto.name = value;
    }

    public get isActive() {
        return this.dto.isActive;
    }

    public get isSuperUser() {
        return !!this.dto.isSuperUser;
    }

    public set isSuperUser(value: boolean) {
        this.dto.isSuperUser = value;
    }

    public get isAdmin() {
        return !!this.dto.isAdmin;
    }

    public set isAdmin(value: boolean) {
        this.dto.isAdmin = value;
    }

    public get email() {
        return this.dto.email || '';
    }

    public set email(value: string) {
        this.dto.email = value;
    }

    public get phone() {
        return this.dto.phone || '';
    }

    public set phone(value: string) {
        this.dto.phone = value;
    }

    public get fax() {
        return this.dto.fax || '';
    }

    public set fax(value: string) {
        this.dto.fax = value;
    }

    public get department() {
        return this.dto.department || '';
    }

    public set department(value: string) {
        this.dto.department = value;
    }

    public get copyUserId() {
        return this.dto.copyUserId || 0;
    }

    public set copyUserId(value: number) {
        this.dto.copyUserId = value;
    }

    /**
     * Determines if the supplied DTO is using the BO or the Consumer shape
     */
    public isBoDto(): this is this & { dto: BoCompanyUserInformationModelDto } {
        const boDto = this.dto as BoCompanyUserInformationModelDto;
        return exists(boDto) && 'pendingEditOfUserUniqueId' in boDto && 'hasLoggedIn' in boDto;
    }

    /**
     * Following properties are only available on the BO shape
     */
    public get rsaId() {
        return this.isBoDto() ? this.dto.mfaId : undefined;
    }

    public get pendingEditOfUserUniqueId() {
        return this.isBoDto() ? this.dto.pendingEditOfUserUniqueId : undefined;
    }

    public get hasLoggedIn() {
        return this.isBoDto() ? this.dto.hasLoggedIn : undefined;
    }
}
